import './App.css';
import { Suspense, lazy, useContext, useEffect, useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

import { GlobalStateProvider, GlobalStateContext } from './contexts/GlobalState';
import { globalStateActionType } from './contexts/GlobalState/reducer';

import { configService } from './services/configService';

import { Localized } from './utilities/localizedStrings';
import { getPrimaryNavGroups } from './utilities/systemAdminNavigation';
import { getTokensFromSSO } from './utilities/identity';

import { Spinner } from './components/common/Spinner/Spinner';
import { PageNotFound } from './components/common/PageNotFound/PageNotFound';
import { TopBar } from './components/common/TopBar/TopBar';
import { PrimaryNav } from './components/common/PrimaryNav/PrimaryNav';
import { ErrorMessageBar } from './components/common/ErrorMessageBar/ErrorMessageBar';
import { MicrosoftSignInButton } from './components/common/SignIn/MicrosoftSignInButton';
import { GoogleSignInButton } from './components/common/SignIn/GoogleSignInButton';

const Dashboard = lazy(() => import('./pages/dashboard'));
const Diagnostics = lazy(() => import('./pages/diagnostics'));
const Configuration = lazy(() => import('./pages/configuration'));
const Tenants = lazy(() => import('./pages/tenants'));
const Users = lazy(() => import('./pages/users'));
const Audits = lazy(() => import('./pages/audits'));

function App() {
    return (
        <GlobalStateProvider>
            <AppThemeManager />
        </GlobalStateProvider>
    );
}

function AppThemeManager() {
    const [state] = useContext(GlobalStateContext);

    useEffect(() => {
        if (state.isDarkMode) {
            document.body.classList.add('dark-mode');
        } else {
            document.body.classList.remove('dark-mode');
        }
    }, [state.isDarkMode]);

    return (
        <div className="cw-App">
            <TopBar />
            <AppMain />
        </div>
    );
}

function AppMain() {
    const [state, dispatch] = useContext(GlobalStateContext);
    const [isSigningIn, setIsSigningIn] = useState(false);
    const [signInError, setSignInError] = useState(null);

    const setSignInState = useCallback(() => {
        async function signIn() {
            try {
                setIsSigningIn(true);
                const cwTokens = await getTokensFromSSO(configService.appId);
                if (cwTokens) {
                    dispatch({
                        type: globalStateActionType.SIGNIN,
                        value: {
                            ...cwTokens
                        }
                    });
                }
            } catch (err) {
                setSignInError(err);
            } finally {
                setIsSigningIn(false);
            }
        }
        signIn();
      },
      [dispatch, setIsSigningIn, setSignInError]
    );

    useEffect(
        setSignInState,
        [setSignInState]
    );

    function renderAuthorized() {
        const primaryNavGroups = getPrimaryNavGroups(state.accessToken?.memberRoles);

        return (
            <Router>
                <div className="cw-App-Body">
                    <PrimaryNav
                        navGroups={primaryNavGroups}
                    />
                    <section className="cw-App-Section-Main">
                        <Suspense fallback={<Spinner />}>
                            <Routes>
                                <Route exact path="/" element={<Dashboard />} />
                                <Route path="/diagnostics" element={<Diagnostics />} />
                                <Route path="/configuration" element={<Configuration />} />
                                <Route path="/tenants" element={<Tenants />} />
                                <Route path="/users" element={<Users />} />
                                <Route path="/audits" element={<Audits />} />
                                <Route path="*" element={<PageNotFound />} />
                            </Routes>
                        </Suspense>
                    </section>
                </div>
            </Router>
        );
    }

    function renderSignIn() {
        return (
            <div className="cw-App-Body">
                <section className="cw-App-Section-Main">
                    <ErrorMessageBar
                        errorInfo={signInError}
                    />
                    <div style={{textAlign: 'center'}}>
                        {isSigningIn ?
                            <Spinner label={Localized.get('authentication-signingIn')} appendEllipsis />
                            :
                            renderSignInContent()
                        }
                    </div>
                </section>
            </div>
        );
    }

    function renderSignInContent() {
        return (
            <>
                <h1>{Localized.get('authentication-signin-message')}</h1>
                <MicrosoftSignInButton
                    setIsSigningIn={setIsSigningIn}
                    setSignInError={setSignInError}
                />
                {/*
                <GoogleSignInButton
                    setIsSigningIn={setIsSigningIn}
                    setSignInError={setSignInError}
                />*/}
            </>            
        );
    }

    function renderSignOut() {
        return (
            <div className="cw-App-Body">
                <section className="cw-App-Section-Main">
                    <Spinner label={Localized.get('authentication-signingOut')} appendEllipsis />
                </section>
            </div>
        );
    }

    if (state.identityToken && state.accessToken) {
        return renderAuthorized();
    } else if (state.isSigningOut) {
        return renderSignOut();
    }

    return renderSignIn();
}

export default App;
